import { useEffect, useRef, useState } from "react";

// Utilitários de movimento da homepage: tudo lento e discreto, e desligado para quem pede menos movimento.

export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
};

// Entra suavemente quando aparece no ecrã.
export function Reveal({ as: Tag = "div", delay = 0, y = 28, className = "", style, children, ...rest }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (reduced || typeof IntersectionObserver === "undefined") {
      setOn(true);
      return undefined;
    }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setOn(true);
        io.disconnect();
      }
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [reduced]);
  return (
    <Tag ref={ref} className={className} style={{ ...style, opacity: on ? 1 : 0, transform: on ? "none" : `translateY(${y}px)`, transition: reduced ? "none" : `opacity 0.9s ease ${delay}ms, transform 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

// Parallax muito subtil: o elemento desloca-se uma fração (speed) da distância ao centro do ecrã.
export function useParallax(ref, speed = 0.08) {
  const reduced = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const offset = r.top + r.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0, ${(-offset * speed).toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, speed, reduced]);
}
